'use strict'

import * as BasUtil from '@basalte/bas-util'

angular
  .module('basalteApp')
  .service('PageProtection', [
    '$rootScope',
    '$transitions',
    '$uiRouterGlobals',
    'CurrentBasCore',
    'BasInputModal',
    'BasModal',
    'BasAppDevice',
    'BasApp',
    'BasState',
    'BasConditionsHelper',
    'BasStateHelper',
    'BAS_INPUT_MODAL',
    'BAS_MODAL',
    'PAGE_PROTECTION',
    'STATES',
    'BAS_CURRENT_CORE',
    'BAS_APP',
    'BasUtilities',
    PageProtection
  ])

/**
 * @constructor
 * @param $rootScope
 * @param $transitions
 * @param $uiRouterGlobals
 * @param {CurrentBasCore} CurrentBasCore
 * @param {BasInputModal} BasInputModal
 * @param {BasModal} BasModal
 * @param {BasAppDevice} BasAppDevice
 * @param {BasApp} BasApp
 * @param {BasState} BasState
 * @param {BasConditionsHelper} BasConditionsHelper
 * @param {BasStateHelper} BasStateHelper
 * @param BAS_INPUT_MODAL
 * @param BAS_MODAL
 * @param PAGE_PROTECTION
 * @param STATES
 * @param BAS_CURRENT_CORE
 * @param BAS_APP
 * @param {BasUtilities} BasUtilities
 */
function PageProtection (
  $rootScope,
  $transitions,
  $uiRouterGlobals,
  CurrentBasCore,
  BasInputModal,
  BasModal,
  BasAppDevice,
  BasApp,
  BasState,
  BasConditionsHelper,
  BasStateHelper,
  BAS_INPUT_MODAL,
  BAS_MODAL,
  PAGE_PROTECTION,
  STATES,
  BAS_CURRENT_CORE,
  BAS_APP,
  BasUtilities
) {
  init()

  const currentBasCoreState = CurrentBasCore.get()
  const basStateState = BasState.get()

  let pageProtectionSettingsLoaded = false
  let lastPageProtectionSettings = null

  const pageToStateMap = {
    [PAGE_PROTECTION.PREFIX_PAGE + PAGE_PROTECTION.P_DOORS]: STATES.DOORS,
    [PAGE_PROTECTION.PREFIX_PAGE + PAGE_PROTECTION.P_SCENES]: STATES.SCENES,
    [PAGE_PROTECTION.PREFIX_PAGE + PAGE_PROTECTION.P_SETTINGS]: STATES.SETTINGS
  }

  async function getLastKnowPageProtectionSettings () {
    if (!pageProtectionSettingsLoaded) {
      await new Promise((resolve) => {
        BasConditionsHelper.waitForSharedStorage(undefined, resolve)
      })
      pageProtectionSettingsLoaded = true
      lastPageProtectionSettings =
        currentBasCoreState.core?.core?.sharedServerStorage.pageProtection
    }
    return lastPageProtectionSettings
  }

  /**
   * @param {string} fromState
   * @param {string}  toState
   * @returns {Promise<{pageProtected: boolean, pin: string?}>}
   */
  async function checkPageProtection (fromState, toState) {

    if (
      !BasAppDevice.isCoreClient() ||
      currentBasCoreState.core?.core?.profile.admin
    ) return { pageProtected: false }

    const pageProtection = await getLastKnowPageProtectionSettings()

    if (!pageProtection) return { pageProtected: false }

    for (const [
      pageKey,
      pageRestricted
    ] of Object.entries(pageProtection.pages)) {

      if (pageRestricted) {
        const restrictedState = pageToStateMap[pageKey]
        if (
          restrictedState &&
          !BasStateHelper.hasBaseState(fromState, restrictedState) &&
          BasStateHelper.hasBaseState(toState, restrictedState)
        ) {
          return { pageProtected: true, pin: pageProtection.pin }
        }
      }
    }

    return { pageProtected: false }
  }

  function init () {

    $transitions.onStart(
      {},
      async (transition) => {

        const toName = transition.targetState().name()
        const fromName = transition.from().name

        // Allow free navigation throughout non-main states
        if (!BasStateHelper.hasBaseState(toName, STATES.MAIN)) return

        const isAdmin = !!currentBasCoreState.core?.core?.profile.admin

        if (toName === STATES.SETTINGS_PAGE_PROTECTION && !isAdmin) {
          return BasState.target(STATES.SETTINGS_GENERAL)
        }

        const {
          pageProtected,
          pin
        } = await checkPageProtection(fromName, toName)

        if (pageProtected && !BasAppDevice.isLisa()) {

          const modal = await BasInputModal.show(BAS_INPUT_MODAL.T_CODE_INPUT, {
            title: BasUtilities.translate('pin_protected_page'),
            saveTextId: 'ok'
          })
          const result = await modal.close

          if (result === pin) {
            // Correct PIN! Do nothing to allow transition
          } else {
            // Only show incorrect pin modal if a PIN was actually entered
            if (BasUtil.isString(result)) {
              // Wait is needed otherwise the modal is closed after state
              //  transition
              BasUtilities.waitForFrames(
                2,
                () => BasModal.show(BAS_MODAL.T_INCORRECT_PIN)
              )
            }
            // If 'fromName' is empty (initial app start, refresh), fall back
            //  to main state
            return BasState.target(fromName || STATES.MAIN)
          }
        }
      }
    )

    $rootScope.$on(
      BAS_APP.EVT_PAUSE,
      checkCurrentPage
    )

    $rootScope.$on(
      BAS_CURRENT_CORE.EVT_CORE_PROFILE_CREATED,
      checkCurrentPage
    )

    $rootScope.$on(
      BAS_CURRENT_CORE.EVT_CORE_SHARED_SERVER_STORAGE,
      async () => {
        pageProtectionSettingsLoaded = true
        lastPageProtectionSettings =
          currentBasCoreState.core?.core?.sharedServerStorage.pageProtection
        await checkCurrentPage()
      }
    )

    let currentPageCheckOngoing = false

    async function checkCurrentPage () {

      if (currentPageCheckOngoing) return

      currentPageCheckOngoing = true

      try {
        const desiredState = $uiRouterGlobals.current.name
        if (
          !currentBasCoreState.core?.core?.profile.admin &&
          basStateState.current.SETTINGS_PAGE_PROTECTION
        ) {
          await BasState.go(STATES.SETTINGS_GENERAL)
        } else {
          const { pageProtected } = await checkPageProtection('', desiredState)

          if (pageProtected) {
            // Go to main state ASAP to prevent user interaction on protected
            //  page
            await BasState.go(STATES.MAIN)

            if (!BasApp.isPaused()) {
              // Navigate to protected page, this will trigger the transition
              //  hook and ask for PIN
              await BasState.go(desiredState)
            }
          }
        }
      } catch (e) {
        // Empty
      }
      currentPageCheckOngoing = false
    }
  }
}
